import React, { useLayoutEffect, useRef, useState } from "react";
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  ChartAxis,
  ChartMarker,
  ChartPane,
  ChartRow,
  ChartSeries,
  ChartSpec,
  ChartStyle,
  ChartValueFormat,
} from "./types";
import {
  FALLBACK_PLOT_WIDTH_PX,
  bucketPlotRows,
  seriesAggregations,
  shouldRenderCandlesticks,
  targetDisplayPointCount,
} from "./displayDensity";
import { chartTimeMilliseconds, chartTimeRange, formatChartTime } from "./timeRange";

const DEFAULT_COLORS = ["#60A5FA", "#A78BFA", "#22C55E", "#F59E0B", "#F472B6", "#22D3EE"];
const TONE_BACKGROUNDS = {
  primary: "#0b1220",
  indicator: "#0d1525",
  volume: "#0a1420",
  comparison: "#0d1422",
  neutral: "#0b1220",
};

type PlotRow = ChartRow & { __chartX: number; __chartIndex: number };

export function chartCoordinate(spec: ChartSpec, value: unknown, index: number): number {
  if (spec.xAxis.scaleMode === "ordinal" || spec.xAxis.scaleMode === "session") return index;
  if (spec.xAxis.type === "number") return Number(value);
  if (spec.xAxis.type === "time") return chartTimeMilliseconds(value) ?? Number.NaN;
  return index;
}

function plottedRows(spec: ChartSpec): PlotRow[] {
  return spec.data.map((row, index) => ({
    ...row,
    __chartX: chartCoordinate(spec, row[spec.xAxis.key], index),
    __chartIndex: index,
  }));
}

function formatNumber(value: unknown, format: ChartAxis["format"]): string {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  if (format === "currency") {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(number);
  }
  if (format === "percent") return `${number.toFixed(2)}%`;
  if (format === "compact") {
    return new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(number);
  }
  if (format === "integer") return Math.round(number).toLocaleString();
  return number.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatHoverValue(
  value: unknown,
  format: ChartValueFormat | "date" | "text",
): string {
  if (value === null || value === undefined || value === "") return "";
  if (format === "text") return String(value);
  if (format === "date") {
    const date = new Date(String(value));
    return Number.isNaN(date.getTime())
      ? String(value)
      : date.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  }
  return formatNumber(value, format);
}

function paneDisplayLabel(pane: ChartPane): string {
  if (pane.label) return pane.label;
  const cleaned = pane.id
    .replace(/[_-]+/g, " ")
    .replace(/\bpane\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "Chart";
  return cleaned.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatX(spec: ChartSpec, row: PlotRow | undefined, range: ReturnType<typeof chartTimeRange>, detail = false): string {
  if (!row) return "";
  const value = row[spec.xAxis.key];
  if (spec.xAxis.type !== "time") return String(value ?? "");
  return formatChartTime(value, range, spec.xAxis.timezone, detail);
}

function dashArray(dash: ChartStyle["dash"]): string | undefined {
  if (dash === "dashed") return "7 5";
  if (dash === "dotted") return "2 4";
  return undefined;
}

function lineType(interpolation: ChartSeries["interpolation"]): "linear" | "stepAfter" | "monotone" {
  if (interpolation === "step") return "stepAfter";
  if (interpolation === "monotone") return "monotone";
  return "linear";
}

function seriesValue(row: ChartRow | undefined, series: ChartSeries): number | null {
  if (!row) return null;
  const key = series.mark === "candlestick" ? series.fieldMapping?.close : series.dataKey;
  const rawValue = key ? row[key] : undefined;
  if (rawValue === null || rawValue === undefined || rawValue === "") return null;
  const value = Number(rawValue);
  return Number.isFinite(value) ? value : null;
}

function barColor(series: ChartSeries, row: ChartRow, previousRow?: ChartRow): string {
  const mapping = series.barColors;
  if (!mapping) return series.style?.color || series.style?.fill || "#38BDF8";
  if (mapping.mode === "field") {
    const value = String(mapping.colorKey ? row[mapping.colorKey] ?? "" : "").toLowerCase();
    if (["up", "positive", "green", "true", "1"].includes(value)) return mapping.positiveColor;
    if (["down", "negative", "red", "false", "-1"].includes(value)) return mapping.negativeColor;
    return mapping.neutralColor || "#64748B";
  }
  const close = Number(mapping.closeKey ? row[mapping.closeKey] : Number.NaN);
  const comparison = Number(
    mapping.openKey
      ? row[mapping.openKey]
      : mapping.comparisonKey
        ? previousRow?.[mapping.comparisonKey]
        : previousRow?.[mapping.closeKey || ""],
  );
  if (!Number.isFinite(close) || !Number.isFinite(comparison)) return mapping.neutralColor || "#64748B";
  if (close > comparison) return mapping.positiveColor;
  if (close < comparison) return mapping.negativeColor;
  return mapping.neutralColor || "#64748B";
}

function CandlestickShape(props: any): React.ReactElement | null {
  const { x, y, width, height, payload, series } = props;
  const mapping = series.fieldMapping;
  const open = Number(payload[mapping.open]);
  const high = Number(payload[mapping.high]);
  const low = Number(payload[mapping.low]);
  const close = Number(payload[mapping.close]);
  if (![x, y, width, height, open, high, low, close].every(Number.isFinite)) return null;
  const range = Math.max(high - low, Number.EPSILON);
  const openY = y + ((high - open) / range) * height;
  const closeY = y + ((high - close) / range) * height;
  const color = close >= open ? series.style?.positiveColor || "#22C55E" : series.style?.negativeColor || "#EF4444";
  const center = x + width / 2;
  const bodyWidth = Math.max(3, width * 0.62);
  return (
    <g data-series-mark="candlestick" data-series-id={series.id} data-semantic-element-id={series.semanticElementId}>
      <line x1={center} x2={center} y1={y} y2={y + height} stroke={color} strokeWidth={1.5} />
      <rect x={center - bodyWidth / 2} y={Math.min(openY, closeY)} width={bodyWidth} height={Math.max(Math.abs(closeY - openY), 2)} rx={1} fill={color} />
    </g>
  );
}

function MarkerGlyph({ cx, cy, marker }: any): React.ReactElement {
  const offsetY = marker.placement === "above" ? -10 : marker.placement === "below" ? 10 : 0;
  const markerY = cy + offsetY;
  const labelY = markerY + (marker.placement === "below" ? 29 : -18);
  const width = Math.max(42, String(marker.label).length * 6.5 + 14);
  const accessibleLabel = marker.detail || marker.label;
  const glyph = marker.shape === "arrowUp"
    ? <path d="M 0 -8 L 7 1 L 3 1 L 3 7 L -3 7 L -3 1 L -7 1 Z" fill={marker.color} />
    : marker.shape === "arrowDown"
      ? <path d="M 0 8 L 7 -1 L 3 -1 L 3 -7 L -3 -7 L -3 -1 L -7 -1 Z" fill={marker.color} />
      : marker.shape === "square"
        ? <rect x={-5} y={-5} width={10} height={10} rx={2} fill={marker.color} />
        : <circle cx={0} cy={0} r={5} fill={marker.color} />;
  return (
    <g
      data-chart-marker={marker.id}
      data-chart-marker-label={marker.label}
      data-chart-marker-shape={marker.shape}
      data-semantic-element-id={marker.semanticElementId}
      role="img"
      aria-label={accessibleLabel}
      tabIndex={0}
    >
      <title>{accessibleLabel}</title>
      <circle cx={cx} cy={markerY} r={12} fill="transparent" pointerEvents="all" />
      <g transform={`translate(${cx} ${markerY})`} stroke="#08111F" strokeWidth={1.5} strokeLinejoin="round">
        {glyph}
      </g>
      <g className={marker.compact ? "unified-chart__marker-label unified-chart__marker-label--compact" : "unified-chart__marker-label"}>
          <rect x={cx + 7} y={labelY - 13} width={width} height={18} rx={5} fill="#08111F" stroke={marker.color} data-testid={`chart-label-background-${marker.id}`} />
          <text x={cx + 14} y={labelY} fill="#F8FAFC" fontSize={11} fontWeight={700}>{marker.label}</text>
      </g>
    </g>
  );
}

function ScatterPoint({ cx, cy, color, seriesId }: any): React.ReactElement | null {
  if (![cx, cy].every(Number.isFinite)) return null;
  return <circle cx={cx} cy={cy} r={4} fill={color} stroke="#08111F" strokeWidth={1.5} data-series-point={seriesId} />;
}

function ReferenceLabel({ viewBox, value, color, id, semanticElementId, position = "end", offset = 0 }: any): React.ReactElement | null {
  const x = Number(viewBox?.x ?? viewBox?.x1);
  const y = Number(viewBox?.y ?? viewBox?.y1) + offset;
  const width = Number(viewBox?.width ?? (Number(viewBox?.x2) - Number(viewBox?.x1)));
  if (![x, y, width].every(Number.isFinite)) return null;
  const label = String(value);
  const labelWidth = Math.max(42, label.length * 6.5 + 14);
  const rectX = position === "start" ? x + 6 : x + width - labelWidth - 6;
  return (
    <g data-chart-reference-label={id} data-semantic-element-id={semanticElementId}>
      <rect x={rectX} y={y - 15} width={labelWidth} height={18} rx={5} fill="#08111F" stroke={color} />
      <text x={rectX + labelWidth / 2} y={y - 2} fill="#F8FAFC" fontSize={11} fontWeight={700} textAnchor="middle">{label}</text>
    </g>
  );
}

function markerValue(spec: ChartSpec, marker: ChartMarker): number | null {
  if (marker.value !== undefined && Number.isFinite(Number(marker.value))) {
    return Number(marker.value);
  }
  const row = spec.data.find((candidate) => candidate[spec.xAxis.key] === marker.x);
  const series = spec.series.find((candidate) => candidate.id === marker.seriesId);
  return row && series ? seriesValue(row, series) : null;
}

function renderSeries(
  series: ChartSeries,
  index: number,
  rows: PlotRow[],
  plotWidth: number,
): React.ReactNode {
  const color = series.style?.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
  const common = {
    yAxisId: series.axisId,
    name: series.label,
    stroke: color,
    fill: series.style?.fill || color,
    opacity: series.style?.opacity ?? 1,
    isAnimationActive: false,
    "data-series-id": series.id,
    "data-semantic-element-id": series.semanticElementId,
  };
  if (series.mark === "candlestick" && !shouldRenderCandlesticks(plotWidth, rows.length)) {
    const closeKey = series.fieldMapping?.close;
    if (!closeKey) return null;
    return (
      <Line
        key={series.id}
        {...common}
        dataKey={closeKey}
        type="linear"
        connectNulls={false}
        strokeWidth={series.style?.lineWidth || 2}
        dot={false}
        activeDot={{ r: 4 }}
        data-series-mark="line"
        data-series-density="collapsed-candlestick"
      />
    );
  }
  if (series.mark === "candlestick") {
    const mapping = series.fieldMapping!;
    return <Bar key={series.id} {...common} dataKey={(row: Record<string, unknown>) => [Number(row[mapping.low]), Number(row[mapping.high])]} shape={(props: any) => <CandlestickShape {...props} series={series} />} maxBarSize={22} />;
  }
  if (series.mark === "bar") {
    return (
      <Bar key={series.id} {...common} dataKey={series.dataKey!} maxBarSize={24} radius={[2, 2, 0, 0]} data-series-mark="bar" data-series-placement={series.placement || "standard"}>
        {rows.map((row, rowIndex) => <Cell key={`${series.id}-${row.__chartIndex}`} fill={barColor(series, row, rows[rowIndex - 1])} />)}
      </Bar>
    );
  }
  if (series.mark === "area") return <Area key={series.id} {...common} dataKey={series.dataKey!} type={lineType(series.interpolation)} strokeWidth={series.style?.lineWidth || 2} fillOpacity={series.style?.opacity ?? 0.18} data-series-mark="area" />;
  if (series.mark === "scatter") {
    return <Line key={series.id} {...common} className="chart-series--scatter" dataKey={series.dataKey!} type="linear" connectNulls={false} stroke="none" dot={(props: any) => <ScatterPoint {...props} color={color} seriesId={series.id} />} activeDot={{ r: 5 }} data-series-mark="scatter" />;
  }
  return <Line key={series.id} {...common} dataKey={series.dataKey!} type={lineType(series.interpolation)} connectNulls={false} strokeWidth={series.style?.lineWidth || 2} strokeDasharray={dashArray(series.style?.dash || "solid")} dot={false} activeDot={{ r: 4 }} data-series-mark="line" />;
}

export function volumeOverlayDomain(
  series: ChartSeries[],
  rows: ChartRow[],
  axisId: string,
): [number, number] | null {
  const overlays = series.filter((item) =>
    item.axisId === axisId &&
    item.role === "volume" &&
    item.placement === "price-volume-overlay" &&
    item.dataKey
  );
  if (overlays.length === 0) return null;
  const maximum = Math.max(
    0,
    ...overlays.flatMap((item) =>
      rows.map((row) => Number(item.dataKey ? row[item.dataKey] : Number.NaN))
        .filter(Number.isFinite)
    ),
  );
  if (maximum <= 0) return [0, 1];
  const ratio = Math.min(...overlays.map((item) => item.overlayHeightRatio ?? 0.24));
  return [0, maximum / ratio];
}

type TimeSeriesPlotProps = {
  syncId: string;
  spec: ChartSpec;
  pane: ChartPane;
  height: number;
  showXAxis: boolean;
  visibleRange: [number, number];
  activeIndex: number;
  hiddenSeries: Set<string>;
  scaleMode: 'linear' | 'log' | 'percentage' | 'indexed';
  onHover: (index: number) => void;
};

type RechartsHoverState = {
  activeLabel?: unknown;
  activeTooltipIndex?: unknown;
  activePayload?: Array<{ payload?: Partial<PlotRow> }>;
};

export function hoveredChartIndex(
  state: RechartsHoverState | null | undefined,
  rows: PlotRow[],
): number | null {
  if (!state || rows.length === 0) return null;

  const activeX = Number(state.activeLabel);
  if (Number.isFinite(activeX)) {
    const nearest = rows.reduce((best, row) =>
      Math.abs(row.__chartX - activeX) < Math.abs(best.__chartX - activeX)
        ? row
        : best,
    );
    return nearest.__chartIndex;
  }

  const payloadIndex = Number(
    state.activePayload?.find((entry) =>
      Number.isInteger(Number(entry.payload?.__chartIndex)),
    )?.payload?.__chartIndex,
  );
  if (Number.isInteger(payloadIndex)) return payloadIndex;

  const visibleIndex = Number(state.activeTooltipIndex);
  if (Number.isInteger(visibleIndex) && rows[visibleIndex]) {
    return rows[visibleIndex].__chartIndex;
  }

  return null;
}

export function pointerChartIndex(
  clientX: number,
  left: number,
  width: number,
  rows: Array<Pick<PlotRow, "__chartIndex">>,
): number | null {
  if (!Number.isFinite(clientX) || !Number.isFinite(left) || !Number.isFinite(width) || width <= 0 || rows.length === 0) {
    return null;
  }
  const ratio = Math.min(1, Math.max(0, (clientX - left) / width));
  const visibleIndex = Math.round(ratio * (rows.length - 1));
  return rows[visibleIndex]?.__chartIndex ?? null;
}

export function chartXAxisTicks(rows: PlotRow[], maximum = 5): number[] {
  if (rows.length === 0 || maximum <= 0) return [];
  if (maximum === 1) return [rows[0].__chartX];
  if (rows.length <= maximum) return rows.map((row) => row.__chartX);
  const lastIndex = rows.length - 1;
  return Array.from({ length: maximum }, (_, tickIndex) => (
    rows[Math.round((tickIndex * lastIndex) / (maximum - 1))].__chartX
  ));
}

function hoverIndexFromPointer(
  clientX: number,
  element: HTMLDivElement,
  rows: PlotRow[],
  onHover: (index: number) => void,
): void {
  const bounds = element.getBoundingClientRect();
  const index = pointerChartIndex(clientX, bounds.left, bounds.width, rows);
  if (index !== null) onHover(index);
}

function hoverIndexFromSurface(
  clientX: number,
  surface: HTMLDivElement,
  rows: PlotRow[],
  onHover: (index: number) => void,
): void {
  const pane = surface.parentElement;
  if (pane instanceof HTMLDivElement) hoverIndexFromPointer(clientX, pane, rows, onHover);
}

export default function TimeSeriesPlot({ syncId, spec, pane, height, showXAxis, visibleRange, activeIndex, hiddenSeries, scaleMode, onHover }: TimeSeriesPlotProps): React.ReactElement {
  const paneRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(FALLBACK_PLOT_WIDTH_PX);
  useLayoutEffect(() => {
    const node = paneRef.current;
    if (!node) return undefined;
    const applyWidth = (width: number) => {
      setPlotWidth(width > 0 ? width : FALLBACK_PLOT_WIDTH_PX);
    };
    applyWidth(node.clientWidth);
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      applyWidth(entries[0]?.contentRect.width || 0);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const allRows = plottedRows(spec);
  const visibleRows = allRows.slice(visibleRange[0], visibleRange[1] + 1);
  const paneSeries = spec.series.filter((item) => item.paneId === pane.id);
  const rows = bucketPlotRows(
    visibleRows,
    targetDisplayPointCount(plotWidth),
    seriesAggregations(paneSeries),
  );
  const axes = spec.axes.filter((axis) => pane.axisIds.includes(axis.id));
  const series = paneSeries
    .filter((item) => !hiddenSeries.has(item.id))
    .sort((left, right) => Number(right.placement === "price-volume-overlay") - Number(left.placement === "price-volume-overlay"));
  const references = (spec.referenceLines || []).filter((line) => line.paneId === pane.id);
  const bands = (spec.referenceBands || []).filter((band) => band.paneId === pane.id);
  const windows = (spec.eventWindows || []).filter((window) => window.paneId === pane.id);
  const markers = (spec.markers || []).filter((marker) => marker.paneId === pane.id);
  const activeRow = spec.data[activeIndex];
  const timeRange = chartTimeRange(spec);
  const xByValue = new Map(allRows.map((row) => [row[spec.xAxis.key], row.__chartX]));
  const firstVisibleX = rows[0]?.__chartX ?? 0;
  const lastVisibleX = rows.at(-1)?.__chartX ?? 1;
  const xPadding = Math.max(1, Math.abs(lastVisibleX - firstVisibleX) * 0.025);
  const background = pane.appearance?.backgroundColor || TONE_BACKGROUNDS[pane.appearance?.tone || "neutral"];
  const hasVolumeOverlay = series.some((item) => item.placement === "price-volume-overlay");
  const representedKeys = new Set<string>([spec.xAxis.key]);
  paneSeries.forEach((item) => {
    if (item.dataKey) representedKeys.add(item.dataKey);
    Object.values(item.fieldMapping || {}).forEach((key) => representedKeys.add(key));
  });

  return (
    <div
      ref={paneRef}
      className="unified-chart__pane"
      data-testid={`chart-pane-${pane.id}`}
      data-pane-id={pane.id}
      data-pane-tone={pane.appearance?.tone || "neutral"}
      data-has-volume-overlay={hasVolumeOverlay ? "true" : "false"}
      data-source-point-count={visibleRows.length}
      data-display-point-count={rows.length}
      onMouseMove={(event) => hoverIndexFromPointer(event.clientX, event.currentTarget, rows, onHover)}
      onPointerMove={(event) => hoverIndexFromPointer(event.clientX, event.currentTarget, rows, onHover)}
      style={{ height, backgroundColor: background }}
    >
      <div className="unified-chart__pane-readout" data-testid={`chart-readout-${pane.id}`} data-active-index={activeIndex}>
        <strong>{paneDisplayLabel(pane)}</strong>
        <span>{formatX(spec, allRows[activeIndex], timeRange, true)}</span>
        {series.flatMap((item) => {
          if (item.mark === "candlestick" && item.fieldMapping) {
            const axisFormat = axes.find((axis) => axis.id === item.axisId)?.format || "number";
            return (["open", "high", "low", "close"] as const).map((field) => (
              <span key={`${item.id}-${field}`}><b>{field[0].toUpperCase()}</b> {formatNumber(activeRow?.[item.fieldMapping![field]], axisFormat)}</span>
            ));
          }
          const value = seriesValue(activeRow, item);
          return value === null ? [] : [<span key={item.id} style={{ color: item.style?.color }}><b>{item.label}</b> {formatNumber(value, axes.find((axis) => axis.id === item.axisId)?.format || "number")}</span>];
        })}
        {(spec.hoverFields || []).filter((field) => field.paneId === pane.id && !representedKeys.has(field.dataKey)).map((field) => (
          <span key={field.id} style={{ color: field.color }}><b>{field.label}</b> {formatHoverValue(activeRow?.[field.dataKey], field.format)}</span>
        ))}
        {markers.filter((marker) => {
          const markerTime = chartTimeMilliseconds(marker.x);
          const rowTime = chartTimeMilliseconds(activeRow?.[spec.xAxis.key]);
          return markerTime !== null && rowTime !== null && markerTime === rowTime;
        }).map((marker) => (
          <span key={marker.id} data-testid={`chart-marker-readout-${marker.id}`}>
            <b>{marker.label}</b>
            {marker.detail && marker.detail !== marker.label ? ` ${marker.detail}` : null}
          </span>
        ))}
      </div>
      <div
        aria-hidden="true"
        className="unified-chart__hover-surface"
        data-testid={`chart-hover-${pane.id}`}
        onMouseMove={(event) => hoverIndexFromSurface(event.clientX, event.currentTarget, rows, onHover)}
        onPointerMove={(event) => hoverIndexFromSurface(event.clientX, event.currentTarget, rows, onHover)}
      />
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={rows}
          margin={{ top: 38, right: 8, bottom: showXAxis ? 5 : 0, left: 0 }}
          syncId={syncId}
          syncMethod="value"
          onMouseMove={(state: any) => {
            const index = hoveredChartIndex(state, rows);
            if (index !== null) onHover(index);
          }}
        >
          <CartesianGrid stroke="rgba(100, 116, 139, 0.16)" strokeDasharray="3 5" vertical={false} />
          <XAxis
            dataKey="__chartX"
            type="number"
            domain={[firstVisibleX - xPadding, lastVisibleX + xPadding]}
            hide={!showXAxis}
            interval="preserveStartEnd"
            tickFormatter={(value) => formatX(spec, allRows.find((row) => row.__chartX === value), timeRange)}
            ticks={chartXAxisTicks(rows)}
            tick={{ fill: "#8190A5", fontSize: 10 }}
            axisLine={{ stroke: "#27344A" }}
            tickLine={false}
            minTickGap={28}
          />
          {axes.map((axis) => {
            const isOverlayAxis = paneSeries.some((item) =>
              item.axisId === axis.id && item.placement === "price-volume-overlay"
            );
            const overlayDomain = volumeOverlayDomain(series, rows, axis.id);
            return (
              <YAxis
                key={axis.id}
                yAxisId={axis.id}
                orientation={axis.side}
                scale={!isOverlayAxis && (scaleMode === "log" || axis.scale === "log") ? "log" : "auto"}
                domain={overlayDomain || (isOverlayAxis ? [0, 1] : [axis.domain?.min ?? (axis.domain?.includeZero ? 0 : "auto"), axis.domain?.max ?? "auto"])}
                hide={isOverlayAxis}
                tickFormatter={(value) => formatNumber(value, axis.format)}
                tick={{ fill: "#8190A5", fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={isOverlayAxis ? 0 : 56}
                mirror
              />
            );
          })}
          <Tooltip content={() => null} cursor={{ stroke: "#94A3B8", strokeWidth: 1 }} />
          {windows.map((window) => <ReferenceArea key={window.id} className="chart-event-window" x1={xByValue.get(window.xStart)} x2={xByValue.get(window.xEnd)} yAxisId={pane.axisIds[0]} fill={window.style?.fill || "#64748B"} fillOpacity={window.style?.opacity ?? 0.14} ifOverflow="hidden" />)}
          {bands.map((band) => <ReferenceArea key={band.id} className="chart-reference-band" yAxisId={band.axisId} y1={band.yStart} y2={band.yEnd} fill={band.style?.fill || "#1D4ED8"} fillOpacity={band.style?.opacity ?? 0.12} ifOverflow="extendDomain" />)}
          {series.map((item, index) => renderSeries(item, index, rows, plotWidth))}
          {references.map((line) => {
            const segment: readonly [{ x?: number; y: number }, { x?: number; y: number }] | undefined = line.xStart !== undefined && line.xEnd !== undefined ? [{ x: xByValue.get(line.xStart), y: line.value }, { x: xByValue.get(line.xEnd), y: line.value }] : undefined;
            const labelPeers = references.filter((candidate) =>
              (candidate.labelPosition || "end") === (line.labelPosition || "end") &&
              candidate.xStart === line.xStart && candidate.xEnd === line.xEnd
            );
            const peerIndex = labelPeers.findIndex((candidate) => candidate.id === line.id);
            const offset = (peerIndex - (labelPeers.length - 1) / 2) * 14;
            return <ReferenceLine key={line.id} yAxisId={line.axisId} {...(segment ? { segment } : { y: line.value })} stroke={line.style?.color || "#94A3B8"} strokeWidth={line.style?.lineWidth || 1} strokeOpacity={line.style?.opacity ?? 1} strokeDasharray={dashArray(line.style?.dash || "dashed")} ifOverflow="extendDomain" label={{ value: line.label, content: (props: any) => <ReferenceLabel {...props} id={line.id} semanticElementId={line.semanticElementId} color={line.style?.color || "#94A3B8"} position={line.labelPosition || "end"} offset={offset} /> }} />;
          })}
          {markers.map((marker) => {
            const value = markerValue(spec, marker);
            const targetSeries = spec.series.find((candidate) => candidate.id === marker.seriesId);
            const x = xByValue.get(marker.x);
            if (value === null || !targetSeries || x === undefined) return null;
            return <ReferenceDot key={marker.id} x={x} y={value} yAxisId={targetSeries.axisId} ifOverflow="visible" shape={(props: any) => <MarkerGlyph {...props} marker={marker} />} />;
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
